import { useState, useEffect, useMemo } from 'react';
import { Car, getCarGradient } from '../../lib/supabase';
import { dataManager } from '../../lib/dataManager';
import { Car as CarIcon, ChevronRight, User, Calendar, ExternalLink, Search, ArrowUpDown, Star, Clock, SortAsc, Edit, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

type SortOption = 'newest' | 'oldest' | 'alphabetical' | 'last_updated' | 'last_added_data' | 'most_favorited';

const SORT_OPTIONS: { id: SortOption; label: string; icon: React.ElementType }[] = [
  { id: 'newest', label: 'Newest (added car)', icon: Clock },
  { id: 'oldest', label: 'Oldest (added car)', icon: Clock },
  { id: 'alphabetical', label: 'A → Z', icon: SortAsc },
  { id: 'most_favorited', label: 'Most Favorited', icon: Star },
  { id: 'last_updated', label: 'Last Updated (car profile)', icon: Edit },
  { id: 'last_added_data', label: 'Last Added Entry (history)', icon: ArrowUpDown },
];

export function AdminCars() {
  const [cars, setCars] = useState<Car[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const navigate = useNavigate();

  useEffect(() => {
    loadCars();
  }, []);

  const loadCars = async () => {
    setLoading(true);
    try {
      const data = await dataManager.fetchAllCars();
      setCars(data);
    } catch (error) {
      console.error('Error loading all cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const filtered = cars.filter(car => {
      const email = car.owner_email || '';
      const plate = car.plate_number || '';
      return (
        car.make.toLowerCase().includes(query) ||
        car.model.toLowerCase().includes(query) ||
        (car.name && car.name.toLowerCase().includes(query)) ||
        plate.toLowerCase().includes(query) ||
        email.toLowerCase().includes(query)
      );
    });

    const sorted = [...filtered];
    switch (sortBy) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'alphabetical':
        sorted.sort((a, b) => (a.name || `${a.make} ${a.model}`).localeCompare(b.name || `${b.make} ${b.model}`));
        break;
      case 'most_favorited':
        sorted.sort((a, b) => (b.favorite_count || 0) - (a.favorite_count || 0));
        break;
      case 'last_updated':
        // Sort by actual "updated_at" from car table
        sorted.sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime());
        break;
      case 'last_added_data':
        // Sort by last_entry_at (from RPC)
        sorted.sort((a, b) => {
          const tA = a.last_entry_at ? new Date(a.last_entry_at).getTime() : 0;
          const tB = b.last_entry_at ? new Date(b.last_entry_at).getTime() : 0;
          return tB - tA;
        });
        break;
    }
    return sorted;
  }, [cars, searchQuery, sortBy]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-4" />
        <span className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">Scanning fleet...</span>
      </div>
    );
  }


  return (
    <div className="space-y-6 font-primary pb-12">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-white mb-1">Vehicle Fleet</h1>
          <p className="text-slate-500 font-bold">Total registered: <span className="text-white">{cars.length}</span> units</p>
        </div>
      </div>

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold" size={18} />
          <input
            type="text"
            placeholder="Search by email, make, model, plate..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-2xl py-3.5 pl-12 pr-4 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-bold shadow-inner text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Refresh button */}
          <button 
            onClick={() => loadCars()}
            disabled={loading}
            className="p-3.5 border border-slate-800 bg-slate-900 rounded-2xl hover:bg-slate-800 disabled:opacity-30 transition-all shadow-sm active:scale-95 text-slate-500 hover:text-blue-400 group"
            title="Refresh"
          >
            <RotateCcw size={18} className={loading ? 'animate-spin' : 'group-hover:rotate-[-30deg] transition-transform'} />
          </button>

          {/* Sort select */}
          <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-2xl px-4 shadow-sm group hover:border-slate-700 transition-colors">
            <ArrowUpDown size={16} className="text-slate-500 shrink-0 group-hover:text-blue-400" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortOption)}
              className="bg-transparent text-slate-300 font-black text-xs uppercase tracking-wider py-3.5 outline-none cursor-pointer"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id} className="bg-slate-900">{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-slate-500 text-[11px] font-black uppercase tracking-widest px-1">
          Filtering <span className="text-blue-400">{filteredAndSorted.length}</span> vehicles
        </p>
      )}

      {/* Cars grid */}
      <div className="grid grid-cols-1 gap-3">
        {filteredAndSorted.map(car => {
          const gradient = getCarGradient(car.placeholder_hue);
          const textColor = `hsl(${car.icon_text_color_hue || 0}, 70%, ${car.icon_text_color_hue === 0 ? '100%' : car.icon_text_color_hue === 360 ? '20%' : '50%'})`;

          return (
            <button
              key={car.id}
              onClick={() => navigate(`/admin/${car.slug}`)}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-5 hover:border-slate-700 hover:bg-slate-800/60 transition-all text-left flex items-center gap-5 group relative overflow-hidden"
            >
              {/* Star badge if many favorites */}
              {(car.favorite_count ?? 0) > 0 && (
                 <div className="absolute top-0 right-12 bg-yellow-500/10 border-x border-b border-yellow-500/20 px-2 py-1 rounded-b-lg flex items-center gap-1">
                    <Star size={10} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-[10px] font-black text-yellow-500">{car.favorite_count}</span>
                 </div>
              )}

              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-2xl group-hover:scale-105 transition-all duration-300 border border-white/5"
                style={{ background: car.image_url && car.use_image_icon ? `url(${car.image_url}) center/cover` : gradient }}
              >
                {!(car.image_url && car.use_image_icon) && (
                  <span className="text-xl font-black px-1 text-center" style={{ color: textColor }}>
                    {car.icon_text || (car.name?.[0] || car.make?.[0] || 'C').toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1.5">
                  <h3 className="font-black text-white text-lg tracking-tight truncate leading-none">{car.name || `${car.make} ${car.model}`}</h3>
                  {car.is_public && (
                    <span className="text-[9px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-lg border border-emerald-500/20 font-black uppercase tracking-tighter shrink-0 shadow-lg shadow-emerald-500/5">Public</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <div className="flex items-center gap-2 text-slate-500 text-[13px] font-bold">
                    <User size={14} className={car.owner_email ? "text-blue-500" : "text-slate-700"} />
                    <span className="truncate max-w-[180px] font-mono tracking-tighter">{car.owner_email || car.user_id}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-[13px] font-bold">
                    <Calendar size={14} className="text-slate-700" />
                    <span className="tracking-tight">{new Date(car.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-slate-600 text-[13px] font-bold tracking-tight">
                    {car.make} {car.model} {car.year && `• ${car.year}`}
                  </div>
                </div>

                {/* Subinfo activity */}
                <div className="mt-2.5 flex items-center gap-4">
                  <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-700">
                    <Clock size={11} />
                    Last Data: <span className="text-slate-500">{car.last_entry_at ? formatDistanceToNow(new Date(car.last_entry_at), { addSuffix: true }) : 'never'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-700">
                    <Edit size={11} />
                    Profile: <span className="text-slate-500">{formatDistanceToNow(new Date(car.updated_at || car.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="p-3 bg-slate-950 border border-slate-800 rounded-2xl text-slate-600 group-hover:text-blue-400 group-hover:border-blue-500/30 transition-all shadow-lg shadow-black/20">
                  <ExternalLink size={18} />
                </div>
                <ChevronRight size={20} className="text-slate-800 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          );
        })}

        {filteredAndSorted.length === 0 && (
          <div className="py-24 text-center bg-slate-900/40 rounded-[40px] border-2 border-dashed border-slate-800/60">
            <CarIcon size={64} className="text-slate-800 mx-auto mb-6 opacity-50" />
            <p className="text-slate-600 font-black uppercase tracking-[0.2em] italic text-xs">Zero units detected.</p>
          </div>
        )}
      </div>
    </div>
  );
}
